import * as React from "react";
import { useEffect, useMemo } from "react";
import {
  Background,
  BackgroundVariant,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
} from "@xyflow/react";

import { cn } from "@/lib/utils";
import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import { WORKSPACE_NODE_SNAP_GRID } from "./workspaceGraphConstants";
import { workspaceNodeTypes } from "./nodes/WorkspaceFlowNode";
import { workspaceEdgeTypes } from "./nodes/WorkspacePipelineEdge";
import {
  toFlowEdges,
  toFlowNodes,
  type WorkspaceFlowNodeData,
} from "./workspaceGraphUtils";

const PREVIEW_HEIGHT = 148;

export type WorkspaceCanvasPreviewGraph = {
  nodes: WorkspaceNode[];
  edges: WorkspaceEdge[];
};

export type WorkspaceCanvasPreviewProps = {
  graph?: WorkspaceCanvasPreviewGraph | null;
  loading?: boolean;
  className?: string;
};

function PreviewFitView({ nodeCount }: { nodeCount: number }): null {
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (nodeCount === 0) return;
    const frame = window.requestAnimationFrame(() => {
      void fitView({
        padding: 0.18,
        duration: 0,
        includeHiddenNodes: true,
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [fitView, nodeCount]);

  return null;
}

function WorkspaceCanvasPreviewFlow({
  graph,
}: {
  graph: WorkspaceCanvasPreviewGraph;
}): React.ReactElement {
  const nodes = useMemo(
    (): Array<Node<WorkspaceFlowNodeData>> =>
      toFlowNodes(graph.nodes, null, { edges: graph.edges }).map((node) => ({
        ...node,
        draggable: false,
        selectable: false,
        connectable: false,
        focusable: false,
        data: {
          ...node.data,
          showActionsMenu: false,
          onSelectNode: undefined,
          onAddChild: undefined,
          onCombinePolicies: undefined,
          onRunCombinedSimulation: undefined,
          onRerunSimulation: undefined,
          onOpenDetail: undefined,
          onToggleDisabled: undefined,
          onDisconnect: undefined,
          onReparent: undefined,
          onBreakLink: undefined,
          onClearSelection: undefined,
        },
      })),
    [graph.edges, graph.nodes]
  );

  const edges = useMemo(
    (): Edge[] =>
      toFlowEdges(graph.edges, graph.nodes).map((edge) => ({
        ...edge,
        selectable: false,
        focusable: false,
        interactionWidth: 0,
      })),
    [graph.edges, graph.nodes]
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={workspaceNodeTypes}
      edgeTypes={workspaceEdgeTypes}
      fitView
      fitViewOptions={{ padding: 0.18, includeHiddenNodes: true }}
      minZoom={0.05}
      maxZoom={1.5}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      edgesFocusable={false}
      nodesFocusable={false}
      panOnDrag={false}
      panOnScroll={false}
      zoomOnScroll={false}
      zoomOnPinch={false}
      zoomOnDoubleClick={false}
      preventScrolling
      selectionOnDrag={false}
      autoPanOnNodeDrag={false}
      proOptions={{ hideAttribution: true }}
      defaultEdgeOptions={{ selectable: false, focusable: false }}
      className="!bg-transparent"
      style={{ width: "100%", height: "100%" }}
    >
      <Background
        id="workspace-hub-preview-dots"
        variant={BackgroundVariant.Dots}
        gap={WORKSPACE_NODE_SNAP_GRID}
        size={1}
        color="var(--border)"
      />
      <PreviewFitView nodeCount={nodes.length} />
    </ReactFlow>
  );
}

/**
 * Hub-card canvas snapshot — same React Flow node/edge chrome as the live canvas
 * (account logo diamond, type strokes, pipeline lanes), scaled to fit.
 */
export function WorkspaceCanvasPreview({
  graph,
  loading = false,
  className,
}: WorkspaceCanvasPreviewProps): React.ReactElement {
  const hasNodes = Boolean(graph?.nodes.length);

  return (
    <div
      data-workspace-canvas-preview=""
      aria-hidden
      className={cn(
        "relative w-full shrink-0 overflow-hidden rounded-t-[6px]",
        "bg-[color:var(--content-canvas)] dark:bg-content-canvas",
        className
      )}
      style={{ height: PREVIEW_HEIGHT }}
    >
      {hasNodes && graph ? (
        <div className="pointer-events-none absolute inset-0 size-full [&_.react-flow__node]:!cursor-default">
          <ReactFlowProvider>
            <WorkspaceCanvasPreviewFlow graph={graph} />
          </ReactFlowProvider>
        </div>
      ) : null}

      {loading && !hasNodes ? (
        <div className="absolute inset-0 animate-pulse bg-muted/20 dark:bg-sidebar-accent/20" />
      ) : null}

      {!loading && !hasNodes ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="font-[family-name:var(--font-primary)] text-xs text-muted-foreground">
            Empty canvas
          </p>
        </div>
      ) : null}
    </div>
  );
}

export function toWorkspaceCanvasPreviewGraph(
  detail: Pick<{ nodes: WorkspaceNode[]; edges: WorkspaceEdge[] }, "nodes" | "edges">
): WorkspaceCanvasPreviewGraph {
  return {
    nodes: detail.nodes.map((node) => ({ ...node })),
    edges: detail.edges.map((edge) => ({ ...edge })),
  };
}
